import React, { useState, useEffect } from "react";
import axios from "axios";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { toast } from "react-toastify";

const API_URL = "https://api.muktihospital.com/api/death-records";
const API_KEY =
  "caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079";

const RequiredIcon = () => <span className="text-red-500 ml-1">*</span>;

const DeathRecordForm = ({ onSuccess, initialData, onCancel }) => {
  const [formData, setFormData] = useState({
    patientName: "",
    deathDate: new Date(),
    guardianName: "",
    report: "",
    age: "",
  });

  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (initialData) {
      setFormData({
        patientName: initialData.patientName || "",
        deathDate: initialData.deathDate
          ? new Date(initialData.deathDate)
          : new Date(),
        guardianName: initialData.guardianName || "",
        report: initialData.report || "",
        age: initialData.age || "",
      });
    }
  }, [initialData]);

  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
  };

  const resetForm = () => {
    setFormData({
      patientName: "",
      deathDate: new Date(),
      guardianName: "",
      report: "",
      age: "",
    });
    setFile(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    // Validate required fields
    if (
      !formData.patientName ||
      !formData.deathDate ||
      !formData.guardianName
    ) {
      toast.error("Please fill in all required fields.");
      setLoading(false);
      return;
    }

    const data = new FormData();
    Object.entries(formData).forEach(([key, value]) => {
      if (key === "deathDate") {
        data.append("deathDate", new Date(value).toISOString());
      } else {
        data.append(key, value);
      }
    });

    if (file) {
      data.append("attachment", file);
    }

    try {
      const url = initialData ? `${API_URL}/${initialData.id}` : API_URL;
      const method = initialData ? "put" : "post";
      const response = await axios[method](url, data, {
        headers: {
          "Content-Type": "multipart/form-data",
          "x-api-key": API_KEY,
        },
      });
      // console.log("API Response (handleSubmit):", response.data); // Debug
      onSuccess?.(response.data);
    } catch (err) {
      console.error(
        "Error saving death record:",
        err.response?.data || err.message
      );
      toast.error(`Failed to ${initialData ? "update" : "add"} death record.`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 px-4 sm:px-0 print-hidden"
    >
      <div className="bg-white rounded-lg shadow-lg w-full max-w-[95%] sm:max-w-[80%] md:max-w-[700px] lg:max-w-[800px] max-h-[90vh] overflow-y-auto">
        <div className="p-4 sm:p-6 border-b border-gray-200 flex justify-between items-center">
          <h2 className="text-lg sm:text-xl font-bold text-gray-700">
            {initialData ? "Edit Death Record" : "Add Death Record"}
          </h2>
          <button
            type="button"
            className="text-gray-500 hover:text-gray-800 text-lg sm:text-xl"
            onClick={() => {
              // console.log("Cross button clicked, closing modal");
              resetForm();
              if (typeof onCancel === "function") {
                onCancel();
              } else {
                console.warn(
                  "onCancel is not a function, modal may not close. Received:",
                  onCancel
                );
              }
            }}
            disabled={loading}
          >
            ✕
          </button>
        </div>

        {/* First Row: Case ID, Patient Name, Death Date, Guardian Name */}
        <div className="p-4 sm:p-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Patient Age <RequiredIcon />
            </label>
            <input
              type="text"
              name="age"
              className="w-full p-2 border rounded-md outline-none focus:ring-0 font-inter font-medium text-sm text-gray-700"
              value={formData.age}
              onChange={handleChange}
              required
              placeholder="Enter Age"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Patient Name <RequiredIcon />
            </label>
            <input
              type="text"
              name="patientName"
              className="w-full p-2 border rounded-md outline-none focus:ring-0 font-inter font-medium text-sm text-gray-700"
              value={formData.patientName}
              onChange={handleChange}
              required
              placeholder="Enter patient's name"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Death Date <RequiredIcon />
            </label>
            <DatePicker
              selected={formData.deathDate}
              onChange={(date) =>
                setFormData((prev) => ({ ...prev, deathDate: date }))
              }
              className="w-full p-2 border rounded-md outline-none focus:ring-0 font-inter font-medium text-sm text-gray-700"
              required
              dateFormat="MM/dd/yyyy"
              maxDate={new Date()}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Guardian Name <RequiredIcon />
            </label>
            <input
              type="text"
              name="guardianName"
              className="w-full p-2 border rounded-md outline-none focus:ring-0 font-inter font-medium text-sm text-gray-700"
              value={formData.guardianName}
              onChange={handleChange}
              required
              placeholder="Enter guardian's name"
            />
          </div>
        </div>

        {/* Second Row: Report (Full Width) */}
        <div className="px-4 sm:px-6 grid grid-cols-1 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Report
            </label>
            <textarea
              name="report"
              className="w-full p-2 border rounded-md outline-none focus:ring-0 font-inter font-medium text-sm text-gray-700"
              value={formData.report}
              onChange={handleChange}
              placeholder="Enter report details"
              rows={4}
            />
          </div>
        </div>

        {/* Third Row: Attachment (Full Width) */}
        <div className="p-4 sm:pt-4 sm:p-6 grid grid-cols-1 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Attachment
            </label>
            <input
              type="file"
              name="attachment"
              className="w-full p-2 border rounded-md outline-none focus:ring-0 font-inter font-medium text-sm text-gray-700"
              onChange={handleFileChange}
              accept="image/*"
            />
            {initialData?.attachment && !file && (
              <img
                src={`https://api.muktihospital.com/uploads/${initialData.attachment}`}
                alt="Attachment"
                className="w-20 h-20 object-cover mt-2 rounded border"
                onError={(e) => {
                  e.target.src = "/path/to/placeholder-image.jpg";
                }}
              />
            )}
            {file && (
              <img
                src={URL.createObjectURL(file)}
                alt="Attachment"
                className="w-20 h-20 object-cover mt-2 rounded border"
              />
            )}
          </div>
        </div>

        <div className="flex flex-col sm:flex-row justify-end gap-3 p-4 sm:p-6 border-t border-gray-200">
          <button
            type="button"
            className="bg-[#E7633D]/90 text-sm text-white py-2 px-6 rounded-md hover:bg-[#E7633D] transition-all duration-300 w-full sm:w-auto"
            onClick={() => {
              // console.log(
              //   initialData ? "Cancel button clicked" : "Clear button clicked"
              // );
              resetForm();
              if (initialData) {
                if (typeof onCancel === "function") {
                  onCancel();
                } else {
                  console.warn(
                    "onCancel is not a function for Cancel button, modal may not close. Received:",
                    onCancel
                  );
                }
              }
            }}
            disabled={loading}
          >
            {initialData ? "Cancel" : "Clear"}
          </button>
          <button
            type="submit"
            className="bg-M-primary-color/90 text-sm text-white py-2 px-6 rounded-md hover:bg-M-primary-color transition-all duration-200 w-full sm:w-auto"
            disabled={loading}
          >
            {loading ? "Saving..." : initialData ? "Update" : "Save"}
          </button>
        </div>
      </div>
    </form>
  );
};

export default DeathRecordForm;